import { LoadingIndicator } from './components/LoadingIndicator';
import { lazy, type ReactNode, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './auth/AuthContext';
import { AuthorizationProvider } from './auth/AuthorizationContext';
import { RequireTenantPermission } from './auth/RequireTenantPermission';
import { AppShell } from './layout/AppShell';
import { PlatformAuthProvider, usePlatformAuth } from './platform/PlatformAuthContext';
import { PlatformShell } from './platform/PlatformShell';
import { TenantProvider } from './tenant/TenantContext';
import { TenantBrowserIcon } from './branding/TenantBrowserIcon';

const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage').then((module) => ({ default: module.DashboardPage })));
const ReportsPage = lazy(() => import('./pages/dashboard/ReportsPage').then((module) => ({ default: module.ReportsPage })));
const LoginPage = lazy(() => import('./pages/account/LoginPage').then((module) => ({ default: module.LoginPage })));
const PersonalCenterPage = lazy(() => import('./pages/account/PersonalCenterPage').then((module) => ({ default: module.PersonalCenterPage })));
const TenantAuditLogsPage = lazy(() => import('./pages/audit/TenantAuditLogsPage').then((module) => ({ default: module.TenantAuditLogsPage })));
const PlatformAuditLogsPage = lazy(() => import('./platform/PlatformAuditLogsPage').then((module) => ({ default: module.PlatformAuditLogsPage })));
const TenantLoginLogsPage = lazy(() => import('./pages/audit/TenantLoginLogsPage').then((module) => ({ default: module.TenantLoginLogsPage })));
const ProductCategoriesPage = lazy(() => import('./pages/catalog/ProductCategoriesPage').then((module) => ({ default: module.ProductCategoriesPage })));
const ProductManagementPage = lazy(() => import('./pages/catalog/ProductManagementPage').then((module) => ({ default: module.ProductManagementPage })));
const HomeBannerManagementPage = lazy(() => import('./pages/storefront/HomeBannerManagementPage').then((module) => ({ default: module.HomeBannerManagementPage })));
const AnnouncementManagementPage = lazy(() => import('./pages/announcement/AnnouncementManagementPage').then((module) => ({ default: module.AnnouncementManagementPage })));
const CustomerUsersPage = lazy(() => import('./pages/customer/CustomerUsersPage').then((module) => ({ default: module.CustomerUsersPage })));
const CustomerLevelsPage = lazy(() => import('./pages/customer/CustomerLevelsPage').then((module) => ({ default: module.CustomerLevelsPage })));
const PermissionManagementPage = lazy(() => import('./pages/iam/PermissionManagementPage').then((module) => ({ default: module.PermissionManagementPage })));
const WechatIntegrationPage = lazy(() => import('./pages/integration/WechatIntegrationPage').then((module) => ({ default: module.WechatIntegrationPage })));
const WorkerLevelsPage = lazy(() => import('./pages/worker/WorkerLevelsPage').then((module) => ({ default: module.WorkerLevelsPage })));
const WorkerApplicationsPage = lazy(() => import('./pages/worker/WorkerApplicationsPage').then((module) => ({ default: module.WorkerApplicationsPage })));
const WorkerNoticesPage = lazy(() => import('./pages/worker/WorkerNoticesPage').then((module) => ({ default: module.WorkerNoticesPage })));
const WorkerStatisticsPage = lazy(() => import('./pages/worker/WorkerStatisticsPage').then((module) => ({ default: module.WorkerStatisticsPage })));
const WorkerLeaderboardPage = lazy(() => import('./pages/worker/WorkerLeaderboardPage').then((module) => ({ default: module.WorkerLeaderboardPage })));
const OrdersPage = lazy(() => import('./pages/order/OrdersPage').then((module) => ({ default: module.OrdersPage })));
const TopupOrdersPage = lazy(() => import('./pages/order/TopupOrdersPage').then((module) => ({ default: module.TopupOrdersPage })));
const FinesPage = lazy(() => import('./pages/finance/FinesPage').then(module => ({ default: module.FinesPage })));
const WithdrawalsPage = lazy(() => import('./pages/finance/WithdrawalsPage').then((module) => ({ default: module.WithdrawalsPage })));
const PlatformLoginPage = lazy(() => import('./platform/PlatformLoginPage').then((module) => ({ default: module.PlatformLoginPage })));
const PlatformTenantsPage = lazy(() => import('./platform/PlatformTenantsPage').then((module) => ({ default: module.PlatformTenantsPage })));
const PlatformLoginLogsPage = lazy(() => import('./platform/PlatformLoginLogsPage').then((module) => ({ default: module.PlatformLoginLogsPage })));

function PlatformHome() {
  const { operator } = usePlatformAuth();
  return <Navigate to={operator?.role === 'PLATFORM_ADMIN' ? '/platform/tenants' : '/platform/login-logs'} replace />;
}

function PlatformAdminOnly({ children }: { children: ReactNode }) {
  const { operator } = usePlatformAuth();
  return operator?.role === 'PLATFORM_ADMIN' ? children : <Navigate to="/platform/login-logs" replace />;
}

function TenantApplication() {
  return (
    <TenantProvider>
      <TenantBrowserIcon />
      <AuthProvider>
        <AuthorizationProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route element={<AppShell />}>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/finance/fines" element={<RequireTenantPermission anyOf={['fund.fine.read']} section="财务管理" title="用户罚款"><FinesPage /></RequireTenantPermission>} />
              <Route path="/reports" element={<RequireTenantPermission anyOf={['order.read', 'customer.user.read', 'fund.withdrawal.read']} section="仪表盘" title="数据页"><ReportsPage /></RequireTenantPermission>} />
              <Route path="/users/worker-applications" element={<RequireTenantPermission anyOf={['worker.application.read']} section="用户管理" title="打手申请"><WorkerApplicationsPage /></RequireTenantPermission>} />
              <Route path="/users/worker-notices" element={<RequireTenantPermission anyOf={['worker.notice.read']} section="用户管理" title="打手专属通知"><WorkerNoticesPage /></RequireTenantPermission>} />
              <Route path="/users/worker-statistics" element={<RequireTenantPermission anyOf={['fund.withdrawal.read']} section="用户管理" title="打手数据统计"><WorkerStatisticsPage /></RequireTenantPermission>} />
              <Route path="/users/worker-leaderboard" element={<RequireTenantPermission anyOf={['order.read']} section="用户管理" title="打手排行榜"><WorkerLeaderboardPage /></RequireTenantPermission>} />
              <Route
                path="/iam"
                element={(
                  <RequireTenantPermission anyOf={['iam.role.read', 'iam.member.read']} section="系统管理" title="权限管理">
                    <PermissionManagementPage />
                  </RequireTenantPermission>
                )}
              />
              <Route
                path="/users"
                element={(
                  <RequireTenantPermission anyOf={['customer.user.read']} section="用户管理" title="用户列表">
                    <CustomerUsersPage key="customer-users" />
                  </RequireTenantPermission>
                )}
              />
              <Route
                path="/users/workers"
                element={(
                  <RequireTenantPermission anyOf={['customer.user.read']} section="用户管理" title="打手列表">
                    <CustomerUsersPage key="worker-users" scope="workers" />
                  </RequireTenantPermission>
                )}
              />
              <Route
                path="/users/levels"
                element={(
                  <RequireTenantPermission anyOf={['customer.level.read']} section="用户管理" title="用户等级">
                    <CustomerLevelsPage />
                  </RequireTenantPermission>
                )}
              />
              <Route
                path="/users/worker-levels"
                element={(
                  <RequireTenantPermission anyOf={['worker.level.read']} section="用户管理" title="打手等级">
                    <WorkerLevelsPage />
                  </RequireTenantPermission>
                )}
              />
              <Route
                path="/products/categories"
                element={(
                  <RequireTenantPermission anyOf={['catalog.category.read']} section="商品中心" title="商品分类">
                    <ProductCategoriesPage />
                  </RequireTenantPermission>
                )}
              />
              <Route
                path="/products"
                element={(
                  <RequireTenantPermission anyOf={['catalog.product.read']} section="商品中心" title="商品管理">
                    <ProductManagementPage />
                  </RequireTenantPermission>
                )}
              />
              <Route path="/orders" element={<RequireTenantPermission anyOf={['order.read']} section="订单中心" title="商品全部订单"><OrdersPage /></RequireTenantPermission>} />
              <Route path="/orders/paid" element={<RequireTenantPermission anyOf={['order.read']} section="订单中心" title="商品付款订单"><OrdersPage key="paid-orders" scope="paid" /></RequireTenantPermission>} />
              <Route path="/orders/unpaid" element={<RequireTenantPermission anyOf={['order.read']} section="订单中心" title="商品未付订单"><OrdersPage key="unpaid-orders" scope="unpaid" /></RequireTenantPermission>} />
              <Route path="/orders/refund-pending" element={<RequireTenantPermission anyOf={['order.read']} section="订单中心" title="商品退款未处理"><OrdersPage key="refund-pending-orders" scope="refundPending" /></RequireTenantPermission>} />
              <Route path="/orders/refund-processed" element={<RequireTenantPermission anyOf={['order.read']} section="订单中心" title="商品退款已处理"><OrdersPage key="refund-processed-orders" scope="refundProcessed" /></RequireTenantPermission>} />
              <Route path="/orders/paid-waiting" element={<RequireTenantPermission anyOf={['order.read']} section="订单中心" title="商品已付款待接单"><OrdersPage key="paid-waiting-orders" scope="paidWaiting" /></RequireTenantPermission>} />
              <Route path="/orders/in-progress" element={<RequireTenantPermission anyOf={['order.read']} section="订单中心" title="商品已接单待完成"><OrdersPage key="in-progress-orders" scope="acceptedPendingCompletion" /></RequireTenantPermission>} />
              <Route path="/orders/pending-settlement" element={<RequireTenantPermission anyOf={['order.read']} section="订单中心" title="商品已完成待结单"><OrdersPage key="pending-settlement-orders" scope="submittedPendingSettlement" /></RequireTenantPermission>} />
              <Route path="/orders/settled" element={<RequireTenantPermission anyOf={['order.read']} section="订单中心" title="商品已完成已结单"><OrdersPage key="settled-orders" scope="confirmedSettled" /></RequireTenantPermission>} />
              <Route path="/orders/deposit-topups" element={<RequireTenantPermission anyOf={['order.read']} section="订单中心" title="保证金充值订单"><TopupOrdersPage key="deposit-topup-orders" kind="deposit" /></RequireTenantPermission>} />
              <Route path="/orders/balance-topups" element={<RequireTenantPermission anyOf={['order.read']} section="订单中心" title="余额充值订单"><TopupOrdersPage key="balance-topup-orders" kind="balance" /></RequireTenantPermission>} />
              <Route path="/finance/withdrawals" element={<RequireTenantPermission anyOf={['fund.withdrawal.read']} section="财务管理" title="全部提现"><WithdrawalsPage /></RequireTenantPermission>} />
              <Route
                path="/storefront/home-banner"
                element={(
                  <RequireTenantPermission anyOf={['catalog.product.read']} section="系统管理" title="首页 Banner">
                    <HomeBannerManagementPage />
                  </RequireTenantPermission>
                )}
              />
              <Route path="/announcements" element={(
                <RequireTenantPermission anyOf={['announcement.read']} section="系统管理" title="平台公告">
                  <AnnouncementManagementPage />
                </RequireTenantPermission>
              )} />
              <Route
                path="/wechat"
                element={(
                  <RequireTenantPermission anyOf={['integration.wechat.read']} section="系统管理" title="微信配置">
                    <WechatIntegrationPage />
                  </RequireTenantPermission>
                )}
              />
              <Route path="/audit-logs" element={(
                <RequireTenantPermission anyOf={['audit.log.read']} section="系统管理" title="操作日志">
                  <TenantAuditLogsPage />
                </RequireTenantPermission>
              )} />
              <Route path="/profile" element={<PersonalCenterPage />} />
              <Route
                path="/login-logs"
                element={(
                  <RequireTenantPermission anyOf={['audit.log.read']} section="系统管理" title="租户登录日志">
                    <TenantLoginLogsPage />
                  </RequireTenantPermission>
                )}
              />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthorizationProvider>
      </AuthProvider>
    </TenantProvider>
  );
}

function PlatformApplication() {
  return (
    <PlatformAuthProvider>
      <Routes>
        <Route path="login" element={<PlatformLoginPage />} />
        <Route element={<PlatformShell />}>
          <Route index element={<PlatformHome />} />
          <Route path="tenants" element={<PlatformAdminOnly><PlatformTenantsPage /></PlatformAdminOnly>} />
          <Route path="audit-logs" element={<PlatformAuditLogsPage />} />
          <Route path="login-logs" element={<PlatformLoginLogsPage />} />
        </Route>
        <Route path="*" element={<PlatformHome />} />
      </Routes>
    </PlatformAuthProvider>
  );
}

function platformRouteEnabled() {
  try {
    return window.localStorage.getItem('platform') === '1';
  } catch {
    return false;
  }
}

export function AppRoutes() {
  return (
    <Suspense fallback={<div className="full-page centered-page"><LoadingIndicator /></div>}>
      <Routes>
        <Route
          path="/platform/*"
          element={platformRouteEnabled()
            ? <PlatformApplication />
            : <main className="full-page centered-page"><h1>页面不存在</h1></main>}
        />
        <Route path="/*" element={<TenantApplication />} />
      </Routes>
    </Suspense>
  );
}
